/**
 * Service worker registration for the Aura PWA.
 *
 * We register in ALL environments (dev + preview + prod) so hotel guests
 * scanning a QR always get the offline shell + installable manifest. The SW
 * never caches /api/*, so clinical data remains authoritative.
 *
 * Call `registerSW()` after the app boots so the initial page load isn't
 * blocked by SW installation.
 */

export function registerSW() {
  if (typeof window === "undefined") return;
  if (!("serviceWorker" in navigator)) return;

  window.addEventListener("load", () => {
    navigator.serviceWorker
      .register("/sw.js", { scope: "/" })
      .then((reg) => {
        // Auto-activate any new worker after an update so users don't have to
        // hard-refresh to pick up new UI.
        reg.addEventListener("updatefound", () => {
          const nw = reg.installing;
          if (!nw) return;
          nw.addEventListener("statechange", () => {
            if (nw.state === "installed" && navigator.serviceWorker.controller) {
              try {
                nw.postMessage("SKIP_WAITING");
              } catch { /* ignore */ }
            }
          });
        });
      })
      .catch(() => { /* SW failures are non-fatal — app still works via network */ });

    // Reload once, at most, when a NEW SW takes control — but only when a
    // controller already existed at load (a genuine update). On a first-ever
    // visit the SW's initial clients.claim() fires controllerchange too, and
    // reloading then aborts any in-flight request (e.g. POST /auth/register)
    // and resets client state mid-session. Skip that first-install case.
    const hadControllerAtLoad = !!navigator.serviceWorker.controller;
    let refreshing = false;
    navigator.serviceWorker.addEventListener("controllerchange", () => {
      if (!hadControllerAtLoad) return; // initial claim on first visit — do NOT reload
      if (refreshing) return;
      refreshing = true;
      // Small delay so the new SW is fully active before we reload
      setTimeout(() => window.location.reload(), 150);
    });
  });
}
